'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, FileText, ShieldCheck, Wrench, Bell, ChevronLeft, ChevronRight, LogOut, Settings, Mail, Users, BarChart2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const navGroups = [
  {
    label: 'Core',
    items: [
      { href: '/', label: 'Dashboard', icon: LayoutDashboard, badge: null },
      { href: '/invoice-capture-management', label: 'Invoices', icon: FileText, badge: '3' },
      { href: '/warranty-locker', label: 'Warranty Locker', icon: ShieldCheck, badge: '2' },
    ],
  },
  {
    label: 'Services',
    items: [
      { href: '/service-request', label: 'Service Requests', icon: Wrench, badge: '1' },
      { href: '/notification-center', label: 'Notifications', icon: Bell, badge: '7' },
      { href: '/mailbox', label: 'Mailbox', icon: Mail, badge: '2' },
    ],
  },
  {
    label: 'Analytics',
    items: [
      { href: '/analytics', label: 'Cross-User Analytics', icon: BarChart2, badge: null },
    ],
  },
  {
    label: 'Profile',
    items: [
      { href: '/family-dashboard', label: 'Family Dashboard', icon: Users, badge: null },
    ],
  },
];

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { signOut } = useAuth();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      router.push('/sign-up-login-screen');
    } catch {
      toast.error('Sign out failed. Please try again.');
    }
  };

  return (
    <aside
      className="relative flex flex-col bg-card border-r border-border h-full transition-all duration-300 ease-in-out"
      style={{ width: collapsed ? '64px' : '240px' }}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 px-4 border-b border-border flex-shrink-0 ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-bold text-lg text-primary tracking-tight">MyDoQs</span>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto py-4 scrollbar-thin">
        {navGroups.map((group) => (
          <div key={`group-${group.label}`} className="mb-4">
            {!collapsed && (
              <p className="px-4 mb-1 text-[11px] font-600 uppercase tracking-widest text-primary/50">
                {group.label}
              </p>
            )}
            {group.items.map((item) => {
              const active = isActive(item.href);
              const Icon = item.icon;
              return (
                <Link
                  key={`nav-${item.href}`}
                  href={item.href}
                  title={collapsed ? item.label : undefined}
                  className={`
                    relative flex items-center mx-2 mb-0.5 rounded-lg transition-all duration-150
                    ${collapsed ? 'justify-center px-0 py-3' : 'gap-3 px-3 py-2.5'}
                    ${active
                      ? 'bg-primary/15 text-primary border border-primary/30' : 'text-muted-foreground hover:bg-muted hover:text-primary/80 border border-transparent'
                    }
                  `}
                >
                  <Icon size={18} className="flex-shrink-0" />
                  {!collapsed && (
                    <span className="text-sm font-500 flex-1">{item.label}</span>
                  )}
                  {!collapsed && item.badge && (
                    <span className="text-[11px] font-600 bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 leading-none">
                      {item.badge}
                    </span>
                  )}
                  {collapsed && item.badge && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-border py-3 px-2 space-y-0.5">
        <Link
          href="/settings"
          className={`flex items-center rounded-lg text-muted-foreground hover:bg-muted hover:text-primary transition-all duration-150 ${collapsed ? 'justify-center py-3' : 'gap-3 px-3 py-2.5'}`}
          title={collapsed ? 'Settings' : undefined}
        >
          <Settings size={18} />
          {!collapsed && <span className="text-sm font-500">Settings</span>}
        </Link>
        <button
          onClick={handleSignOut}
          className={`w-full flex items-center rounded-lg text-muted-foreground hover:bg-danger/10 hover:text-danger transition-all duration-150 ${collapsed ? 'justify-center py-3' : 'gap-3 px-3 py-2.5'}`}
          title={collapsed ? 'Sign Out' : undefined}
        >
          <LogOut size={18} />
          {!collapsed && <span className="text-sm font-500">Sign Out</span>}
        </button>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 z-10 w-6 h-6 bg-card border border-primary/40 rounded-full flex items-center justify-center text-primary hover:border-primary transition-all duration-150 shadow-card"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}